/**
 * DivisionalChakrasManager - Centralized manager for all divisional chakra hooks
 *
 * Architecture:
 * - Calls all D7-D60 hooks unconditionally at top level (React hooks rules)
 * - Returns settings object for each chakra type
 * - Enables auto-restore from localStorage even when accordion sections collapsed
 * - Single source of truth for all divisional chakra state
 *
 * Why needed:
 * - React hooks MUST be called unconditionally at top level
 * - If hook is inside accordion/conditional, it won't run when collapsed
 * - Auto-restore fails if hook doesn't run on mount
 * - Eliminates need to manually add hooks for each new D-division
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const divisionalSettings = useDivisionalChakrasManager();
 *
 *   // Access individual chakra settings:
 *   const d7 = divisionalSettings.d7; // { showChakra, startPoint, updateVisibility, ... }
 *   const d8 = divisionalSettings.d8;
 *
 *   return <UniversalDivisionalToggle settings={divisionalSettings.d8} />;
 * }
 * ```
 */

import { useDivisionalChakraSettings, DivisionalChakraSettings } from './useDivisionalChakraSettings';
import { DivisionalChakraType } from '../config/divisionalChakraConfig';

/**
 * List of all divisional chakra types managed by this hook
 */
const ALL_DIVISIONAL_CHAKRAS: DivisionalChakraType[] = [
    'muhurta',   // Muhurta - 30 sectors
    'nakshatra', // Nakshatra - 27 lunar mansions (with pada subdivisions)
    'd7',        // Saptamsa - Chara Karaka
    'd8',        // Ashtamsa - Vara (directional deities)
    'd12',       // Dwadasamsa - Lakshmi
    'd20',       // Vimsamsa
    'd24',       // Siddhamsa
    'd30',       // Trimsamsa
    'd40',       // Khavedamsa
    'd60'        // Shashtiamsa
];

/**
 * Settings object containing all divisional chakra states
 */
export interface DivisionalChakrasManagerState {
    muhurta: DivisionalChakraSettings;
    nakshatra: DivisionalChakraSettings;
    d7: DivisionalChakraSettings;
    d8: DivisionalChakraSettings;
    d12: DivisionalChakraSettings;
    d20: DivisionalChakraSettings;
    d24: DivisionalChakraSettings;
    d30: DivisionalChakraSettings;
    d40: DivisionalChakraSettings;
    d60: DivisionalChakraSettings;
}

/**
 * Centralized manager hook - initializes all divisional chakra hooks at top level
 *
 * Benefits:
 * 1. ✅ Auto-restore works for all D-divisions
 * 2. ✅ Single place to manage all divisional chakras
 * 3. ✅ Settings can be passed down via props (no prop drilling with hooks)
 * 4. ✅ Easy to add new divisions - just add to array
 *
 * @returns Object with settings for each divisional chakra type
 */
export function useDivisionalChakrasManager(): DivisionalChakrasManagerState {
    // Call all hooks unconditionally at top level (React hooks rules)
    const muhurta = useDivisionalChakraSettings('muhurta');
    const nakshatra = useDivisionalChakraSettings('nakshatra');
    const d7 = useDivisionalChakraSettings('d7');
    const d8 = useDivisionalChakraSettings('d8');
    const d12 = useDivisionalChakraSettings('d12');
    const d20 = useDivisionalChakraSettings('d20');
    const d24 = useDivisionalChakraSettings('d24');
    const d30 = useDivisionalChakraSettings('d30');
    const d40 = useDivisionalChakraSettings('d40');
    const d60 = useDivisionalChakraSettings('d60');

    return {
        muhurta,
        nakshatra,
        d7,
        d8,
        d12,
        d20,
        d24,
        d30,
        d40,
        d60
    };
}

/**
 * Get list of all divisional chakra types managed by this hook
 */
export function getAllDivisionalChakraTypes(): DivisionalChakraType[] {
    return [...ALL_DIVISIONAL_CHAKRAS];
}
